/**
 * K6 — Flow binding panel (W3 Work slot) via resolveWorkbenchActions + deny hint
 */

import { useEffect, useMemo } from 'react';
import { Button, List, Space, Typography } from 'antd';
import { Link } from 'react-router-dom';
import type { UniversalBaseEntity } from '@shared/types/entity';
import { ROLES, type UserRole } from '@shared/types/roles';
import type { WorkbenchActionSurface, WorkbenchResolvedAction } from '@shared/business-workbench';
import {
  getOpenFlowBindings,
  getStartFlowBindings,
  registerWorkbenchActions,
  buildStartFlowActions,
  type StartFlowBinding,
} from '@shared/business-workbench';
import { omitUndefined } from '@shared/utils/omitUndefined';
import { useDetailWorkbenchActionsFromContext } from './detail-workbench-actions-context';
import { CapabilityDeniedHint } from './CapabilityDeniedHint';

const { Text } = Typography;

export interface FlowBindingPanelProps {
  entity: UniversalBaseEntity;
  tenantId?: string;
  startFlowBindings?: readonly StartFlowBinding[];
  onStartFlow?: (flowTemplateId: string) => void;
  /** Inspector slot — open flows only (density default closed) */
  showStartFlows?: boolean;
  role?: UserRole;
  permissionKeys?: string[];
  surface?: WorkbenchActionSurface;
}

function flowTemplateIdFromAction(action: { handlerRef: string }): string {
  return action.handlerRef.slice('flow:start:'.length);
}

function FlowStartActionWithDeniedHint({
  action,
  onStartFlow,
}: {
  action: WorkbenchResolvedAction;
  onStartFlow?: (flowTemplateId: string) => void;
}) {
  const denied = Boolean(action.forbidden);
  const flowTemplateId = flowTemplateIdFromAction(action);

  return (
    <Space direction="vertical" size={4} align="start">
      <Button
        type="primary"
        disabled={denied}
        onClick={() => !denied && onStartFlow?.(flowTemplateId)}
      >
        {action.label}
      </Button>
      {denied ? (
        <CapabilityDeniedHint
          {...omitUndefined({
            reason: action.tooltip,
            suggestedAction: action.suggestedAction,
          })}
          testId={`flow-${action.id}-capability-denied`}
        />
      ) : null}
    </Space>
  );
}

export function FlowBindingPanel({
  entity,
  tenantId: tenantIdProp,
  startFlowBindings: startFlowBindingsProp,
  onStartFlow,
  showStartFlows = true,
  role = ROLES.OPERATIONS_MANAGER,
  permissionKeys,
  surface: _surface = 'detail-header',
}: FlowBindingPanelProps) {
  const tenantId = tenantIdProp ?? entity.tenantId;
  const businessDomain = String(entity.businessDomain ?? 'entity');

  useEffect(() => {
    const bindings = startFlowBindingsProp ?? getStartFlowBindings(entity);
    registerWorkbenchActions(buildStartFlowActions(bindings));
  }, [entity, startFlowBindingsProp]);

  const { actions } = useDetailWorkbenchActionsFromContext({
    tenantId,
    entity,
    businessDomain,
    role,
    ...(permissionKeys !== undefined ? { permissionKeys } : {}),
  });

  const startFlowActions = useMemo(() => {
    if (!showStartFlows) {
      return [];
    }
    return actions
      .filter((action) => action.handlerRef.startsWith('flow:start:'))
      .sort((a, b) => b.priority - a.priority);
  }, [actions, showStartFlows]);

  const openFlows = getOpenFlowBindings(entity);

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%' }}
      data-testid="flow-binding-panel"
    >
      {startFlowActions.length > 0 ? (
        <Space wrap data-testid="flow-start-actions" align="start">
          {startFlowActions.map((action) => (
            <FlowStartActionWithDeniedHint
              key={action.id}
              action={action}
              {...(onStartFlow !== undefined ? { onStartFlow } : {})}
            />
          ))}
        </Space>
      ) : null}

      <div data-testid="flow-open-list">
        <Text strong>Open Flows</Text>
        {openFlows.length === 0 ? (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            진행 중인 Flow가 없습니다.
          </Text>
        ) : (
          <List
            size="small"
            dataSource={openFlows}
            renderItem={(flow) => (
              <List.Item>
                {flow.href ? (
                  <Link to={flow.href}>{flow.label ?? flow.flowId}</Link>
                ) : (
                  <span>{flow.label ?? flow.flowId}</span>
                )}
              </List.Item>
            )}
          />
        )}
      </div>
    </Space>
  );
}

/** W4 — flow action registry bootstrap (deduped per template id) */
export function ensureFlowWorkbenchActionsRegistered(bindings: readonly StartFlowBinding[]): void {
  registerWorkbenchActions(buildStartFlowActions(bindings));
}
